import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, Checkbox, FormControlLabel,
  Divider, List, ListItem, ListItemText, ListItemIcon,
  CircularProgress, Alert, Paper, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Assessment as AssessmentIcon,
  Replay as ReplayIcon,
  FilterAlt as CvFilterIcon,
} from '@mui/icons-material';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';

const AtsCvFilteringDialog = ({ open, onClose, stage, applications, position, positionId }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'es';
  const { authToken, organization, project, showAlert } = useStore.getState();

  const [cvView, setCvView] = useState('list');
  const [filterings, setFilterings] = useState([]);
  const [loadingFilterings, setLoadingFilterings] = useState(false);
  const [selectedFiltering, setSelectedFiltering] = useState(null);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [launching, setLaunching] = useState(false);

  useEffect(() => {
    if (open) {
      setCvView('list');
      setSelectedCandidates([]);
      setSelectedFiltering(null);
      loadFilterings();
    }
  }, [open]);

  const loadFilterings = async () => {
    try {
      setLoadingFilterings(true);
      const response = await api.get(
        `/cv-filterings?projectId=${project}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (response.data.errorCode === 0) {
        const all = response.data.response?.data || [];
        setFilterings(all.filter((f) => f.positionId === positionId));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingFilterings(false);
    }
  };

  useEffect(() => {
    if (!open || filterings.length === 0) return;
    const unsubscribers = [];
    filterings.forEach((filtering) => {
      if (filtering.status === 'processing') {
        const filteringRef = doc(db, 'cvFilterings', filtering.id);
        const unsubscribe = onSnapshot(filteringRef, (docSnap) => {
          if (!docSnap.exists()) return;
          const data = docSnap.data();
          if (data.status !== 'processing') {
            loadFilterings();
            if (selectedFiltering?.id === filtering.id) {
              setSelectedFiltering({ ...data, id: filtering.id });
            }
          }
        });
        unsubscribers.push(unsubscribe);
      }
    });
    return () => unsubscribers.forEach((unsub) => unsub());
  }, [filterings, open]);

  const handleSelectAll = (checked) => {
    setSelectedCandidates(checked ? applications.map((a) => a.uid || a.id) : []);
  };

  const handleToggleCandidate = (appId) => {
    setSelectedCandidates((prev) =>
      prev.includes(appId) ? prev.filter((id) => id !== appId) : [...prev, appId]
    );
  };

  const handleLaunch = async () => {
    if (selectedCandidates.length === 0) return;
    setLaunching(true);
    try {
      const selected = applications.filter((a) => selectedCandidates.includes(a.uid || a.id));
      const createResponse = await api.post(
        `/cv-filterings`,
        {
          title: `Filtrado ATS — ${stage.name} — ${position?.name}`,
          positionId,
          positionName: position?.name,
          organizationId: organization,
          projectId: project,
          applicants: selected.map((a) => a.applicantId).filter(Boolean),
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (createResponse.data.errorCode !== 0) {
        showAlert(t('ats.cvFilter.error'), 'error');
        setLaunching(false);
        return;
      }

      const filteringId = createResponse.data.response?.data?.id;
      if (!filteringId) {
        showAlert(t('ats.cvFilter.error'), 'error');
        setLaunching(false);
        return;
      }

      await api.post(
        `/cv-filterings/${filteringId}/process`,
        { language: lang },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      showAlert(t('ats.cvFilter.launched'), 'success');
      setSelectedCandidates([]);
      await loadFilterings();
      setCvView('list');
    } catch (err) {
      console.error(err);
      showAlert(t('ats.cvFilter.error'), 'error');
    } finally {
      setLaunching(false);
    }
  };

  const handleRetry = async (filteringId) => {
    try {
      await api.post(`/cv-filterings/${filteringId}/process`, { language: lang }, { headers: { Authorization: `Bearer ${authToken}` } });
      await loadFilterings();
    } catch (err) {
      console.error(err);
    }
  };

  const getStatusChip = (status) => {
    const config = {
      pending:    { label: t('cvFiltering.statusPending'),    color: 'default'  },
      processing: { label: t('cvFiltering.statusProcessing'), color: 'warning'  },
      completed:  { label: t('cvFiltering.statusCompleted'),  color: 'success'  },
      failed:     { label: t('cvFiltering.statusFailed'),     color: 'error'    },
    };
    const c = config[status] || config.pending;
    return <Chip label={c.label} color={c.color} size="small" />;
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    if (ts.toDate) return ts.toDate().toLocaleDateString('es-CL');
    if (ts._seconds) return new Date(ts._seconds * 1000).toLocaleDateString('es-CL');
    return new Date(ts).toLocaleDateString('es-CL');
  };

  return (
    <Dialog open={open} onClose={() => { if (!launching) onClose(); }} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {cvView !== 'list' && (
            <IconButton size="small" onClick={() => setCvView('list')} disabled={launching}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          <Typography variant="h6">
            {cvView === 'list'    && t('ats.cvFilter.dialogTitle')}
            {cvView === 'create'  && t('ats.cvFilter.newFiltering')}
            {cvView === 'results' && (selectedFiltering?.title || t('cvFiltering.viewResults'))}
          </Typography>
        </Box>
        {cvView === 'list' && (
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => { setSelectedCandidates([]); setCvView('create'); }}
            sx={{ backgroundColor: '#10B981', '&:hover': { backgroundColor: '#059669' } }}
          >
            {t('ats.cvFilter.newFiltering')}
          </Button>
        )}
      </DialogTitle>

      <DialogContent sx={{ minHeight: 300 }}>

        {/* LISTA */}
        {cvView === 'list' && (
          <>
            {loadingFilterings ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : filterings.length === 0 ? (
              <Alert severity="info">{t('ats.cvFilter.noFilterings')}</Alert>
            ) : (
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                      <TableCell sx={{ fontWeight: 600 }}>{t('cvFiltering.tableTitle')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('cvFiltering.tableApplicants')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('cvFiltering.tableStatus')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('cvFiltering.tableDate')}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>{t('actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filterings.map((filtering) => (
                      <TableRow key={filtering.id} hover>
                        <TableCell><Typography variant="caption">{filtering.title}</Typography></TableCell>
                        <TableCell><Typography variant="caption">{filtering.applicants?.length || 0}</Typography></TableCell>
                        <TableCell>{getStatusChip(filtering.status)}</TableCell>
                        <TableCell><Typography variant="caption">{formatDate(filtering.created)}</Typography></TableCell>
                        <TableCell align="center">
                          {filtering.status === 'processing' && <CircularProgress size={16} />}
                          {filtering.status === 'completed' && (
                            <IconButton size="small" color="primary" onClick={() => { setSelectedFiltering(filtering); setCvView('results'); }}>
                              <AssessmentIcon fontSize="small" />
                            </IconButton>
                          )}
                          {filtering.status === 'failed' && (
                            <IconButton size="small" color="warning" onClick={() => handleRetry(filtering.id)}>
                              <ReplayIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}

        {/* CREAR */}
        {cvView === 'create' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('ats.cvFilter.dialogSubtitle')}</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedCandidates.length === applications.length && applications.length > 0}
                  indeterminate={selectedCandidates.length > 0 && selectedCandidates.length < applications.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              }
              label={<Typography variant="body2" fontWeight={600}>{t('ats.cvFilter.selectAll')}</Typography>}
            />
            <Divider sx={{ my: 1 }} />
            <List dense>
              {applications.map((app) => {
                const appId = app.uid || app.id;
                const hasApplicantId = !!app.applicantId;
                return (
                  <ListItem key={appId} disablePadding>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox size="small" checked={selectedCandidates.includes(appId)}
                        onChange={() => handleToggleCandidate(appId)} disabled={!hasApplicantId} />
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="body2" color={hasApplicantId ? 'inherit' : 'text.disabled'}>{app.candidateName || '—'}</Typography>}
                      secondary={<Typography variant="caption" color="text.secondary">{app.candidateEmail}{!hasApplicantId && ` — ${t('ats.cvFilter.noApplicantId')}`}</Typography>}
                    />
                  </ListItem>
                );
              })}
            </List>
          </>
        )}

        {/* RESULTADOS */}
        {cvView === 'results' && selectedFiltering && (
          <Box>
            <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('cvFiltering.totalApplicants')}</Typography>
                  <Typography variant="h6">{selectedFiltering.applicants?.length || 0}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('cvFiltering.processed')}</Typography>
                  <Typography variant="h6">{selectedFiltering.results?.length || 0}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('cvFiltering.averageScore')}</Typography>
                  <Typography variant="h6">
                    {selectedFiltering.results?.length > 0
                      ? (selectedFiltering.results.reduce((acc, r) => acc + (r.score || 0), 0) / selectedFiltering.results.length).toFixed(1)
                      : '0'}%
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>{t('cvFiltering.candidateRanking')}</Typography>

            {selectedFiltering.results?.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[...selectedFiltering.results]
                  .sort((a, b) => (b.score || 0) - (a.score || 0))
                  .map((result, index) => (
                    <Paper key={result.applicantId} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: index === 0 ? '#F59E0B' : index === 1 ? '#9CA3AF' : index === 2 ? '#92400E' : '#E5E7EB', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 700, flexShrink: 0 }}>
                            {index + 1}
                          </Box>
                          <Typography variant="body2" fontWeight={600}>{result.applicantName}</Typography>
                        </Box>
                        <Chip label={`${result.score || 0}%`} size="small"
                          sx={{ backgroundColor: (result.score || 0) >= 70 ? '#DCFCE7' : (result.score || 0) >= 50 ? '#FEF9C3' : '#FEE2E2', color: (result.score || 0) >= 70 ? '#16A34A' : (result.score || 0) >= 50 ? '#CA8A04' : '#DC2626', fontWeight: 700 }} />
                      </Box>
                      {result.recommendation && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{result.recommendation}</Typography>}
                      {result.strengths?.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {result.strengths.slice(0, 2).map((s, i) => <Chip key={i} label={s} size="small" color="success" variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />)}
                        </Box>
                      )}
                    </Paper>
                  ))}
              </Box>
            ) : (
              <Alert severity="info">{t('ats.cvFilter.noResults')}</Alert>
            )}

            {selectedFiltering.reportUrl && (
              <Button href={selectedFiltering.reportUrl} target="_blank" rel="noopener noreferrer" variant="outlined" fullWidth sx={{ mt: 2 }}>
                {t('ats.cvFilter.downloadReport')}
              </Button>
            )}
          </Box>
        )}

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={launching}>{t('close')}</Button>
        {cvView === 'create' && (
          <Button variant="contained" onClick={handleLaunch} disabled={selectedCandidates.length === 0 || launching}
            startIcon={launching ? <CircularProgress size={16} color="inherit" /> : <CvFilterIcon />}
            sx={{ backgroundColor: '#10B981', '&:hover': { backgroundColor: '#059669' } }}>
            {launching ? t('ats.cvFilter.launching') : t('ats.cvFilter.launch', { count: selectedCandidates.length })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AtsCvFilteringDialog;